import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, CircleAlert, Link2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { loanPrerequisitesQueryOptions } from '@/api/savings-api'
import type { LoanPrerequisites } from '@/api/savings-types'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { MoneyInput } from '@/components/money-input'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import type { Pesewas } from '@/lib/money'

import { prerequisiteGateLabel } from './constants'
import { savingsPaths, useSavingsNavigate } from './navigation'

/**
 * The three gates a loan account cannot be opened without.
 *
 * The deposit gate is the one that needs an input: whether a security deposit is sufficient is a
 * question about a *particular loan product's* requirement, and without that figure the server has
 * nothing to compare the balance against. So the requirement is asked for rather than assumed, and
 * until it is given this panel reports the balance without claiming it is enough.
 *
 * Each reference is nullable and a null means the customer does not hold that thing — which is the
 * finding, not a gap in the data.
 */
export function LoanPrerequisitesPanel({ customerId }: { customerId: string }) {
  const [required, setRequired] = useState<Pesewas | null>(null)
  const [applied, setApplied] = useState<Pesewas | null>(null)

  const query = useQuery(loanPrerequisitesQueryOptions(customerId, applied))

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>Loan prerequisites</CardTitle>
          <p className="mt-0.5 text-xs text-muted">
            Read from the savings module, which owns all three gates. A loan application checks the
            same answer.
          </p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-48 flex-1 space-y-1.5">
            <Label htmlFor="prerequisite-required">Security deposit the loan product requires</Label>
            <MoneyInput id="prerequisite-required" value={required} onChange={setRequired} />
            <p className="text-xs text-muted">
              Optional. Without it the deposit gate is answered against nothing, so the balance is
              reported but not judged.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" size="sm" onClick={() => setApplied(required)}>
              Check
            </Button>
            {applied !== null ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRequired(null)
                  setApplied(null)
                }}
              >
                Clear
              </Button>
            ) : null}
          </div>
        </div>

        {query.isPending ? (
          <Skeleton className="h-48 w-full" />
        ) : query.isError ? (
          <ErrorSurface
            error={query.error}
            onRetry={() => void query.refetch()}
            title="The prerequisites could not be read"
          />
        ) : (
          <PrerequisiteFindings prerequisites={query.data} requiredMinor={applied} />
        )}
      </CardContent>
    </Card>
  )
}

function PrerequisiteFindings({
  prerequisites,
  requiredMinor,
}: {
  prerequisites: LoanPrerequisites
  requiredMinor: Pesewas | null
}) {
  const navigate = useSavingsNavigate()
  const met = prerequisites.unmetGates.length === 0

  return (
    <div className="space-y-3">
      <Alert tone={met ? 'success' : 'warning'}>
        {met ? <CheckCircle2 aria-hidden /> : <CircleAlert aria-hidden />}
        <div className="space-y-1">
          <AlertTitle>
            {met
              ? requiredMinor === null
                ? 'Nothing is missing — but no deposit requirement was given'
                : 'Every gate is met'
              : `${String(prerequisites.unmetGates.length)} gate(s) unmet`}
          </AlertTitle>
          <AlertDescription>
            {met && requiredMinor === null ? (
              <>
                The accounts and the book are in place. The deposit gate was checked against no
                requirement, so this does not say the collateral is sufficient for any particular
                loan product.
              </>
            ) : met ? (
              <>A loan account can be opened for this customer.</>
            ) : (
              <ul className="mt-1 list-inside list-disc space-y-0.5">
                {prerequisites.unmetGates.map((gate) => (
                  <li key={gate}>{prerequisiteGateLabel(gate)}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </div>
      </Alert>

      <dl className="grid gap-3 sm:grid-cols-3">
        <Gate
          label="Savings account"
          value={
            prerequisites.savingsAccount === null ? null : (
              <span className="space-y-1">
                <span className="block font-mono text-sm text-heading">
                  {prerequisites.savingsAccount.accountNumber}
                </span>
                <StatusBadge status={prerequisites.savingsAccount.status} />
              </span>
            )
          }
          emptyLabel="None held"
          onOpen={
            prerequisites.savingsAccount === null
              ? undefined
              : () => {
                  navigate(savingsPaths.account(prerequisites.savingsAccount?.id ?? ''))
                }
          }
        />

        <Gate
          label="Security deposit"
          value={
            prerequisites.securityDepositAccount === null ? null : (
              <span className="space-y-1">
                <span className="block font-mono text-sm text-heading">
                  {prerequisites.securityDepositAccount.accountNumber}
                </span>
                <MoneyDisplay
                  value={prerequisites.securityDepositBalanceMinor}
                  currency={prerequisites.currencyCode}
                  emphasis="strong"
                  className="text-left"
                />
                {requiredMinor === null ? (
                  <span className="block text-2xs text-muted">Not judged — no requirement given</span>
                ) : (
                  <span className="block text-2xs text-muted">
                    against a requirement of{' '}
                    <MoneyDisplay
                      value={requiredMinor}
                      currency={prerequisites.currencyCode}
                      className="inline text-left"
                    />
                  </span>
                )}
              </span>
            )
          }
          emptyLabel="None held"
          onOpen={
            prerequisites.securityDepositAccount === null
              ? undefined
              : () => {
                  navigate(savingsPaths.account(prerequisites.securityDepositAccount?.id ?? ''))
                }
          }
        />

        <Gate
          label="Passbook"
          value={
            prerequisites.passbook === null ? null : (
              <span className="space-y-1">
                <span className="block font-mono text-sm text-heading">
                  {prerequisites.passbook.passbookNumber}
                </span>
                <StatusBadge status={prerequisites.passbook.status} />
              </span>
            )
          }
          emptyLabel="No active book"
        />
      </dl>
    </div>
  )
}

function Gate({
  label,
  value,
  emptyLabel,
  onOpen,
}: {
  label: string
  value: ReactNode
  emptyLabel: string
  onOpen?: () => void
}) {
  return (
    <div className="rounded-card border border-border-subtle bg-surface-muted p-3">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className="mt-1">
        {value ?? <span className="text-sm text-muted">{emptyLabel}</span>}
        {onOpen ? (
          <Button variant="ghost" size="sm" className="mt-1 -ml-3" onClick={onOpen}>
            <Link2 />
            Open
          </Button>
        ) : null}
      </dd>
    </div>
  )
}
